import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class ShootingResult extends BaseObj {
    public Alias: string;
    public __alias: string;

    public ContestantId: number;
    public __contestantid: number;

    public Disabled: boolean;
    public __disabled: boolean;

    public EventID: number;
    public __eventid: number;

    public readonly Id: string | null;

    public Lane: number;
    public __lane: number;

    public Misses: boolean[];
    public __misses: boolean[];

    public RaceContestantId: number;
    public __racecontestantid: number;

    public RaceId: number;
    public __raceid: number;

    public ShootingNumber: number;
    public __shootingnumber: number;

    public StationId: number | null;
    public __stationid: number | null;

    public Time: Date;
    public __time: Date;

    public TUID: string;
    public __tuid: string;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Alias?: string | null;
        ContestantId?: number;
        Disabled?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        Id?: string | null;
        Lane?: number;
        Misses?: boolean[];
        RaceContestantId?: number;
        RaceId?: number;
        ShootingNumber?: number;
        StationId?: number | null;
        Time?: Date;
        TUID?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<ShootingResult>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('ShootingResult tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.Alias = values.Alias ?? '';
        this.__alias = values.Alias ?? '';
        Object.defineProperty(this, 'Alias', {
            get(): string {
                return this.__alias;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Alias', this.__alias, value);
                this.__alias = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantId = values.ContestantId ?? 0;
        this.__contestantid = values.ContestantId ?? 0;
        Object.defineProperty(this, 'ContestantId', {
            get(): number {
                return this.__contestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantId', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Disabled = values.Disabled ?? false;
        this.__disabled = values.Disabled ?? false;
        Object.defineProperty(this, 'Disabled', {
            get(): boolean {
                return this.__disabled;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Disabled', this.__disabled, value);
                this.__disabled = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Id = values.Id ?? '';

        this.Lane = values.Lane ?? 0;
        this.__lane = values.Lane ?? 0;
        Object.defineProperty(this, 'Lane', {
            get(): number {
                return this.__lane;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Lane', this.__lane, value);
                this.__lane = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Misses = values.Misses ?? [];
        this.__misses = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.Misses ?? [],
            callback: (oldVal, newVal) => this.__entityStateHandler.propertyChanged('Misses', oldVal, newVal),
        });
        Object.defineProperty(this, 'Misses', {
            get(): boolean[] {
                return this.__misses;
            },
            set(value: boolean[]) {
                this.__entityStateHandler.propertyChanged('Misses', this.__misses, value);
                this.__misses = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (oldVal: boolean[], newVal: boolean[]) => this.__entityStateHandler.propertyChanged('Misses', oldVal, newVal),
                });
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestantId = values.RaceContestantId ?? 0;
        this.__racecontestantid = values.RaceContestantId ?? 0;
        Object.defineProperty(this, 'RaceContestantId', {
            get(): number {
                return this.__racecontestantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceContestantId', this.__racecontestantid, value);
                this.__racecontestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceId = values.RaceId ?? 0;
        this.__raceid = values.RaceId ?? 0;
        Object.defineProperty(this, 'RaceId', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceId', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ShootingNumber = values.ShootingNumber ?? 0;
        this.__shootingnumber = values.ShootingNumber ?? 0;
        Object.defineProperty(this, 'ShootingNumber', {
            get(): number {
                return this.__shootingnumber;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ShootingNumber', this.__shootingnumber, value);
                this.__shootingnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationId = values.StationId ?? null;
        this.__stationid = values.StationId ?? null;
        Object.defineProperty(this, 'StationId', {
            get(): number | null {
                return this.__stationid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StationId', this.__stationid, value);
                this.__stationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        this.__time = values.Time ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Time', {
            get(): Date {
                return this.__time;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Time', this.__time, value);
                this.__time = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TUID = values.TUID ?? '';
        this.__tuid = values.TUID ?? '';
        Object.defineProperty(this, 'TUID', {
            get(): string {
                return this.__tuid;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('TUID', this.__tuid, value);
                this.__tuid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"Alias": ${JSON.stringify(this.Alias)}` +
            ',' +
            `"ContestantId": ${JSON.stringify(this.ContestantId)}` +
            ',' +
            `"Disabled": ${JSON.stringify(this.Disabled)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Id": ${JSON.stringify(this.Id)}` +
            ',' +
            `"Lane": ${JSON.stringify(this.Lane)}` +
            ',' +
            `"Misses": [ ${this.Misses.map((x) => JSON.stringify(x))} ]` +
            ',' +
            `"RaceContestantId": ${JSON.stringify(this.RaceContestantId)}` +
            ',' +
            `"RaceId": ${JSON.stringify(this.RaceId)}` +
            ',' +
            `"ShootingNumber": ${JSON.stringify(this.ShootingNumber)}` +
            ',' +
            `"StationId": ${JSON.stringify(this.StationId)}` +
            ',' +
            `"Time": ${'"' + formatDateIso(this.Time) + '"'}` +
            ',' +
            `"TUID": ${JSON.stringify(this.TUID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of ShootingResult from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Alias?: string | null;
        ContestantId?: number;
        Disabled?: boolean;
        EventID?: number;
        Lane?: number;
        Misses?: boolean[];
        RaceContestantId?: number;
        RaceId?: number;
        ShootingNumber?: number;
        StationId?: number | null;
        Time?: Date;
        TUID?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): ShootingResult {
        return new ShootingResult(values);
    }

    public static _FromServerJson(json: any): ShootingResult {
        return new ShootingResult({
            Alias: json.Alias ?? '',
            ContestantId: json.ContestantId,
            Disabled: json.Disabled,
            EventID: json.EventID,
            Id: json.Id ?? '',
            Lane: json.Lane,
            Misses: json.Misses,
            RaceContestantId: json.RaceContestantId,
            RaceId: json.RaceId,
            ShootingNumber: json.ShootingNumber,
            StationId: json.StationId ?? null,
            Time: json.Time instanceof Date ? json.Time : new Date(json.Time),
            TUID: json.TUID ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of ShootingResult from another instance of ShootingResult.
     * @param other The ShootingResult you want to clone
     * @returns A cloned ShootingResult
     */
    public static FromOther(other: ShootingResult): ShootingResult {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return ShootingResult._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ShootingResult.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'Alias'
            | 'ContestantId'
            | 'Disabled'
            | 'EventID'
            | 'Id'
            | 'Lane'
            | 'Misses'
            | 'RaceContestantId'
            | 'RaceId'
            | 'ShootingNumber'
            | 'StationId'
            | 'Time'
            | 'TUID'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'ShootingResult';
}
